import React, { useEffect, useState } from "react";
import CareersApplicants from "@/pages/app/CareersApplicants";
import ScoringVersionSwitch from "@/pages/app/ScoringVersionSwitch";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { mockAuth } from "@/mock";
import { useDemoMode } from "@/hooks/useDemoMode";
import { toast } from "@/components/ui/sonner";
import { LoginScreen } from "./app/LoginScreen";
import { AuthCallback } from "./app/AuthCallback";
import { AdminSidebar, AdminTopBar } from "./app/AdminSidebar";
import AdminDashboard from "./app/admin/AdminDashboard";
import AdminPipelineList from "./app/admin/PipelineList";
import AdminWorkspace from "./app/admin/AdminWorkspace";
import { PipelineScreen } from "./app/PipelineScreen";
import { AnalyticsScreen } from "./app/AnalyticsScreen";
import { EmailDraftsScreen } from "./app/EmailDraftsScreen";
import { CustomerPortal } from "./app/CustomerPortal";
import { TenantsScreen } from "./app/TenantsScreen";
import { TenantPortal } from "./app/TenantPortal";
import AssessmentHub from "./app/AssessmentHub";
import SOWAdmin from "./app/SOWAdmin";
import CalendarView from "./app/CalendarView";
import SettingsScreen from "./app/SettingsScreen";
import EmployeeOperations from "./app/EmployeeOperations";
import AssessmentTemplates from "./app/AssessmentTemplates";
import AdminPlaceholder from "./app/AdminPlaceholder";
import ProductsAdminPage from "./app/admin/ProductsAdminPage";
import TermsAdminPage from "./app/admin/TermsAdminPage";
import LicensesAdminPage from "./app/admin/LicensesAdminPage";
import HelpTextAdminPage from "./app/admin/HelpTextAdminPage";
import ServiceInquiriesAdminPage from "./app/admin/ServiceInquiriesAdminPage";
import FeedbackAdminPage from "./app/admin/FeedbackAdminPage";
import AttributionAdminPage from "./app/admin/AttributionAdminPage";
import DataIntegrityPage from "./app/admin/DataIntegrityPage";
import EmailsAdminPage from "./app/admin/EmailsAdminPage";
import EmailTemplatesAdminPage from "./app/admin/EmailTemplatesAdminPage";
import DuplicateReviewPage from "./app/admin/schools/DuplicateReviewPage";
import CampaignsPanel from "./app/campaigns/CampaignsPanel";
import DataSafetySettings from "./app/admin/DataSafetySettings";import PortfoliosAdminPage from "./app/admin/portfolios/PortfoliosAdminPage";
import OrganizationsAdminPage from "./app/admin/organizations/OrganizationsAdminPage";
import ScenarioLabPage from "./app/admin/ScenarioLabPage";
import SandboxAccessPage from "./app/admin/SandboxAccessPage";
import IntelligenceAccessPage from "./app/admin/IntelligenceAccessPage";
import SchoolsAdminPage from "./app/admin/schools/SchoolsAdminPage";
import OnboardingAdminPage from "./app/admin/OnboardingAdminPage";
import UsersAdminPage from "./app/admin/UsersAdminPage";
import AuthHealthCheckPage from "./app/admin/AuthHealthCheckPage";
import ExpertsAdminPage from "./app/admin/ExpertsAdminPage";
import DemoManagerPage from "./app/admin/DemoManagerPage";
import ActAsLogPage from "./app/admin/ActAsLogPage";
import FoundingPartnersPage from "./app/admin/FoundingPartnersPage";
import LandingSettingsPage from "./app/admin/LandingSettingsPage";
import RiskBandsPage from "./app/admin/RiskBandsPage";
import CmsHubPage from "./app/admin/CmsHubPage";
import MsxCmsPage from "./app/admin/MsxCmsPage";
import { ProductCatalog, PendingSignups } from "./app/admin/ProductsAdminPage";
import SiteCopySettings from "./SiteCopySettings";
import PricingConfig from "./app/PricingConfig";
import DataMaintenance from "./app/DataMaintenance";
import ReferralSettings from "./app/ReferralSettings";
import ReleaseVersionSettings from "./app/ReleaseVersionSettings";
// ClawAssistantWidget only rendered on public landing pages

const API = process.env.REACT_APP_BACKEND_URL;

// ── Stage 6 redirect helpers (preserve query string + hash) ──
const RedirQ = ({ to }) => {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}${loc.hash}`} replace />;
};
const RedirParam = ({ build }) => {
  const params = useParams();
  const loc = useLocation();
  return <Navigate to={`${build(params)}${loc.search}${loc.hash}`} replace />;
};
const lastBusiness = () => {
  try { return localStorage.getItem("admin.lastBusiness"); } catch { return null; }
};
const IndexRedir = () => {
  const loc = useLocation();
  let biz = lastBusiness();
  if (!biz) {
    let mode = null;
    try { mode = localStorage.getItem("admin-dashboard-mode"); } catch { /* ignore */ }
    biz = mode === "assessments" ? "clarity" : "myschoolrx";
  }
  const target = biz === "clarity" ? "/app/clarity" : "/app/myschoolrx";
  return <Navigate to={`${target}${loc.search}${loc.hash}`} replace />;
};
const LicensesRedir = () => {
  const loc = useLocation();
  const biz = lastBusiness() === "clarity" ? "clarity" : "myschoolrx";
  return <Navigate to={`/app/${biz}/licenses${loc.search}${loc.hash}`} replace />;
};
const EmailTemplatesRedir = () => {
  const loc = useLocation();
  const biz = lastBusiness() === "clarity" ? "clarity" : "myschoolrx";
  return <Navigate to={`/app/${biz}/email-templates${loc.search}${loc.hash}`} replace />;
};
const RedirHash = ({ path, hash }) => {
  const loc = useLocation();
  return <Navigate to={`${path}${loc.search}${hash}`} replace />;
};

// ── Stage 8 (3.3) product split wrappers ──
const ClarityProductCatalog = ({ session }) => (
  <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="clarity-product-catalog">
    <h1 className="text-2xl font-bold text-[#111827]">Product Catalog</h1>
    <p className="text-sm text-[#6b7280] mt-1">The Clarity platform product.</p>
    <div className="mt-6"><ProductCatalog session={session} productType="clarity_full" defaultOpen /></div>
  </div>
);
const MsxProductSettings = ({ session }) => (
  <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="msx-product-settings">
    <h1 className="text-2xl font-bold text-[#111827]">Product Settings</h1>
    <p className="text-sm text-[#6b7280] mt-1">MySchoolRx — availability, price and notifications.</p>
    <div className="mt-6"><ProductCatalog session={session} productType="caporilli_diagnostic" variant="settings" defaultOpen /></div>
  </div>
);
const MsxPendingSignups = ({ session }) => (
  <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="msx-pending-signups">
    <h1 className="text-2xl font-bold text-[#111827]">Pending Signups</h1>
    <p className="text-sm text-[#6b7280] mt-1">Approve or reject incoming MySchoolRx tenants.</p>
    <div className="mt-6"><PendingSignups session={session} /></div>
  </div>
);

export { LoginScreen };

export default function AppArea() {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const isDemo = useDemoMode();

  // Detect session_id in URL hash synchronously during render (NOT in useEffect)
  const isOAuthCallback = location.hash?.includes("session_id=");

  useEffect(() => {
    // If returning from OAuth, AuthCallback handles everything
    if (isOAuthCallback) {
      setLoading(false);
      return;
    }

    // If user data was passed from AuthCallback via navigation state
    if (location.state?.user) {
      const user = location.state.user;
      if ((user.role || "").toLowerCase() === "sales") {
        navigate("/sales", { replace: true });
        return;
      }
      setSession({
        user: {
          id: user.user_id,
          full_name: user.name,
          email: user.email,
          role: user.role,
          picture: user.picture,
        },
      });
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      // Check real auth session via /api/auth/me
      try {
        const resp = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        if (resp.ok) {
          const user = await resp.json();
          if (user.must_change_password || user.password_reset_required) {
            navigate("/change-password", { replace: true });
            return;
          }
          // Sales employees never see the admin workspace — send to Sales Portal.
          if ((user.role || "").toLowerCase() === "sales") {
            navigate("/sales", { replace: true });
            return;
          }
          setSession({
            user: {
              id: user.user_id,
              full_name: user.name,
              email: user.email,
              role: user.role,
              picture: user.picture,
            },
          });
          setLoading(false);
          return;
        }
      } catch {}

      // Fallback: check mock session (for demo mode)
      const mockSession = mockAuth.getSession();
      if (mockSession) {
        setSession(mockSession);
      }
      setLoading(false);
    };
    checkAuth();
  }, [isOAuthCallback, location.state]);

  const handleSignOut = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {}
    mockAuth.signOut();
    try { localStorage.clear(); sessionStorage.clear(); } catch {}
    setSession(null);
    toast.message("Signed out");
    navigate("/diagnostic/login");
  };

  // Render AuthCallback for OAuth returns
  if (isOAuthCallback) return <AuthCallback />;

  if (loading) return null;

  if (!session) {
    // Unified auth: outside demo mode, send unauthenticated visitors to the one
    // canonical MySchoolRx login (which routes admins back to /app).
    if (!isDemo) return <Navigate to="/diagnostic/login" replace />;
    return (
      <LoginScreen
        onSignedIn={(s) => {
          setSession(s);
        }}
      />
    );
  }

  // Customer Portal stays on the Light Executive Clarity theme (#f9fafb).
  // Admin Portal uses a dedicated content background (#f4f6fa) and the new dark navy sidebar.
  const isClient = session.user.role === "client";
  const isAdminUser = ["super_admin", "admin"].includes(session.user.role);
  const A = (el) => (isAdminUser ? el : <Navigate to="/app" replace />);

  // Portfolio Owners belong in the dedicated /portfolio shell, never the admin app.
  if (session.user.role === "portfolio_owner") {
    return <Navigate to="/portfolio/dashboard" replace />;
  }

  return (
    <div className={`min-h-screen text-[#111827] ${isClient ? "bg-[#f9fafb]" : "bg-[#f4f6fa]"}`}>
      {isClient ? (
        <CustomerPortal session={session} />
      ) : (
        <>
          <AdminSidebar session={session} onSignOut={handleSignOut} />
          <div className="lg:pl-[240px] pt-14 lg:pt-0">
            <AdminTopBar />
            <main className="px-6 py-8 sm:px-8 lg:px-10">
              <Routes>
                {/* ── Index + legacy dashboard ── */}
                <Route path="/" element={<IndexRedir />} />
                <Route path="/dashboard" element={<IndexRedir />} />

                {/* ══ MySchoolRx business ══ */}
                <Route path="/myschoolrx" element={<AdminDashboard session={session} business="myschoolrx" />} />
                <Route path="/myschoolrx/campaigns" element={A(<CampaignsPanel session={session} />)} />
                <Route path="/myschoolrx/attribution" element={A(<AttributionAdminPage session={session} />)} />
                <Route path="/myschoolrx/careers/applicants" element={A(<CareersApplicants />)} />
                <Route path="/myschoolrx/scoring-switch" element={A(<ScoringVersionSwitch />)} />
                <Route path="/myschoolrx/sales-data-health" element={A(<DataIntegrityPage session={session} />)} />
                <Route path="/myschoolrx/referrals" element={A(<ReferralSettings session={session} />)} />
                <Route path="/myschoolrx/schools" element={A(<SchoolsAdminPage session={session} />)} />
                <Route path="/myschoolrx/schools/duplicates" element={A(<DuplicateReviewPage session={session} />)} />
                <Route path="/myschoolrx/portfolios" element={A(<PortfoliosAdminPage session={session} />)} />
                <Route path="/myschoolrx/organizations" element={A(<OrganizationsAdminPage session={session} />)} />
                <Route path="/myschoolrx/licenses" element={A(<LicensesAdminPage session={session} presetProduct="caporilli_diagnostic" />)} />
                <Route path="/myschoolrx/signups" element={A(<MsxPendingSignups session={session} />)} />
                <Route path="/myschoolrx/product" element={A(<MsxProductSettings session={session} />)} />
                <Route path="/myschoolrx/onboarding" element={A(<OnboardingAdminPage session={session} />)} />
                <Route path="/myschoolrx/founding-partners" element={A(<FoundingPartnersPage session={session} />)} />
                <Route path="/myschoolrx/terms" element={A(<TermsAdminPage session={session} />)} />
                <Route path="/myschoolrx/data-safety" element={A(<DataSafetySettings session={session} />)} />
                <Route path="/myschoolrx/help-text" element={A(<HelpTextAdminPage session={session} />)} />
                <Route path="/myschoolrx/ai-access" element={A(<IntelligenceAccessPage session={session} />)} />
                <Route path="/myschoolrx/cms" element={A(<MsxCmsPage session={session} />)} />
                <Route path="/myschoolrx/email-templates" element={A(<EmailTemplatesAdminPage session={session} business="myschoolrx" />)} />
                <Route path="/myschoolrx/service-enquiries" element={A(<ServiceInquiriesAdminPage session={session} />)} />
                <Route path="/myschoolrx/feedback" element={A(<FeedbackAdminPage session={session} />)} />
                <Route path="/myschoolrx/demos" element={A(<DemoManagerPage session={session} />)} />
                <Route path="/myschoolrx/act-as-log" element={A(<ActAsLogPage />)} />
                <Route path="/myschoolrx/scenario-lab" element={A(<ScenarioLabPage session={session} />)} />
                <Route path="/myschoolrx/sandbox" element={A(<SandboxAccessPage session={session} />)} />
                <Route path="/myschoolrx/experts" element={<ExpertsAdminPage session={session} />} />
                <Route path="/myschoolrx/landing" element={A(<LandingSettingsPage session={session} />)} />

                {/* ══ Clarity business ══ */}
                <Route path="/clarity" element={<AdminDashboard session={session} business="clarity" />} />
                <Route path="/clarity/calendar" element={<CalendarView session={session} />} />
                <Route path="/clarity/analytics" element={<AnalyticsScreen session={session} />} />
                <Route path="/clarity/leads" element={<AdminPipelineList session={session} mode="leads" />} />
                <Route path="/clarity/leads/legacy" element={<PipelineScreen session={session} mode="leads" />} />
                <Route path="/clarity/leads/:prospectId" element={<AdminWorkspace session={session} mode="leads" />} />
                <Route path="/clarity/email-drafts" element={<EmailDraftsScreen session={session} />} />
                <Route path="/clarity/customers" element={<AdminPipelineList session={session} mode="customers" />} />
                <Route path="/clarity/customers/legacy" element={<PipelineScreen session={session} mode="customers" />} />
                <Route path="/clarity/customers/:prospectId" element={<AdminWorkspace session={session} mode="customers" />} />
                <Route path="/clarity/tenants" element={<TenantsScreen session={session} />} />
                <Route path="/clarity/tenants/:tenantId" element={<TenantPortal session={session} />} />
                <Route path="/clarity/scope" element={<SOWAdmin session={session} />} />
                <Route path="/clarity/project-tracker" element={<AdminPlaceholder title="Project Tracker" subtitle="Open a customer workspace to track that organization's project milestones. A cross-tenant tracker is coming soon." backTo="/app/clarity/customers" />} />
                <Route path="/clarity/playbooks" element={<AdminPlaceholder title="Playbooks" subtitle="Open a customer workspace to view that organization's playbooks. A consolidated cross-tenant view is coming soon." backTo="/app/clarity/customers" />} />
                <Route path="/clarity/assessments" element={<AssessmentTemplates session={session} />} />
                <Route path="/clarity/assessment-templates" element={<AssessmentHub session={session} />} />
                <Route path="/clarity/products" element={A(<ClarityProductCatalog session={session} />)} />
                <Route path="/clarity/pricing" element={A(<PricingConfig session={session} />)} />
                <Route path="/clarity/licenses" element={A(<LicensesAdminPage session={session} presetProduct="clarity_full" />)} />
                <Route path="/clarity/cms" element={A(<SiteCopySettings session={session} embedded />)} />
                <Route path="/clarity/email-templates" element={A(<EmailTemplatesAdminPage session={session} business="clarity" />)} />

                {/* ══ Platform utility ══ */}
                <Route path="/platform/employees" element={<EmployeeOperations />} />
                <Route path="/platform/users" element={A(<UsersAdminPage session={session} />)} />
                <Route path="/platform/auth-health" element={<AuthHealthCheckPage session={session} />} />
                <Route path="/platform/emails" element={A(<EmailsAdminPage session={session} />)} />
                <Route path="/platform/data-maintenance" element={A(<DataMaintenance session={session} />)} />
                <Route path="/platform/release" element={A(<ReleaseVersionSettings session={session} />)} />
                <Route path="/platform/risk-bands" element={A(<RiskBandsPage session={session} />)} />

                {/* ══ ADMIN-IA §5 redirects — old → new (query + hash preserved) ══ */}
                <Route path="/pipeline" element={<RedirQ to="/app/clarity/leads" />} />
                <Route path="/leads" element={<RedirQ to="/app/clarity/leads" />} />
                <Route path="/pipeline/legacy" element={<RedirQ to="/app/clarity/leads/legacy" />} />
                <Route path="/pipeline/:prospectId" element={<RedirParam build={(p) => `/app/clarity/leads/${p.prospectId}`} />} />
                <Route path="/leads/:prospectId" element={<RedirParam build={(p) => `/app/clarity/leads/${p.prospectId}`} />} />
                <Route path="/customers" element={<RedirQ to="/app/clarity/customers" />} />
                <Route path="/customers/legacy" element={<RedirQ to="/app/clarity/customers/legacy" />} />
                <Route path="/customers/:prospectId" element={<RedirParam build={(p) => `/app/clarity/customers/${p.prospectId}`} />} />
                <Route path="/analytics" element={<RedirQ to="/app/clarity/analytics" />} />
                <Route path="/tenants" element={<RedirQ to="/app/clarity/tenants" />} />
                <Route path="/tenants/:tenantId" element={<RedirParam build={(p) => `/app/clarity/tenants/${p.tenantId}`} />} />
                <Route path="/settings/site-copy" element={<RedirQ to="/app/clarity/cms" />} />
                <Route path="/email-drafts" element={<RedirQ to="/app/clarity/email-drafts" />} />
                <Route path="/assessments" element={<RedirQ to="/app/clarity/assessments" />} />
                <Route path="/assessment-templates" element={<RedirQ to="/app/clarity/assessment-templates" />} />
                <Route path="/pricing" element={<RedirQ to="/app/clarity/pricing" />} />
                <Route path="/settings/landing" element={<RedirHash path="/app/myschoolrx/cms" hash="#get-started" />} />
                <Route path="/settings" element={<RedirQ to="/app/clarity/cms" />} />
                <Route path="/employee-ops" element={<RedirQ to="/app/platform/employees" />} />
                <Route path="/employees" element={<RedirQ to="/app/platform/employees" />} />
                <Route path="/sow" element={<RedirQ to="/app/clarity/scope" />} />
                <Route path="/scope-of-services" element={<RedirQ to="/app/clarity/scope" />} />
                <Route path="/playbooks" element={<RedirQ to="/app/clarity/playbooks" />} />
                <Route path="/project-tracker" element={<RedirQ to="/app/clarity/project-tracker" />} />
                <Route path="/calendar" element={<RedirQ to="/app/clarity/calendar" />} />
                <Route path="/products" element={<RedirQ to="/app/clarity/products" />} />
                <Route path="/terms" element={<RedirQ to="/app/myschoolrx/terms" />} />
                <Route path="/licenses" element={<LicensesRedir />} />
                <Route path="/help-text" element={<RedirQ to="/app/myschoolrx/help-text" />} />
                <Route path="/service-inquiries" element={<RedirQ to="/app/myschoolrx/service-enquiries" />} />
                <Route path="/feedback" element={<RedirQ to="/app/myschoolrx/feedback" />} />
                <Route path="/attribution" element={<RedirQ to="/app/myschoolrx/attribution" />} />
                <Route path="/data-integrity" element={<RedirQ to="/app/myschoolrx/sales-data-health" />} />
                <Route path="/emails" element={<RedirQ to="/app/platform/emails" />} />
                <Route path="/email-templates" element={<EmailTemplatesRedir />} />
                <Route path="/school-duplicates" element={<RedirQ to="/app/myschoolrx/schools/duplicates" />} />
                <Route path="/campaigns" element={<RedirQ to="/app/myschoolrx/campaigns" />} />
                <Route path="/data-safety" element={<RedirQ to="/app/myschoolrx/data-safety" />} />
                <Route path="/scenario-lab" element={<RedirQ to="/app/myschoolrx/scenario-lab" />} />
                <Route path="/demos" element={<RedirQ to="/app/myschoolrx/demos" />} />
                <Route path="/founding-partners" element={<RedirQ to="/app/myschoolrx/founding-partners" />} />
                <Route path="/sandbox-access" element={<RedirQ to="/app/myschoolrx/sandbox" />} />
                <Route path="/ai-access" element={<RedirQ to="/app/myschoolrx/ai-access" />} />
                <Route path="/portfolios" element={<RedirQ to="/app/myschoolrx/portfolios" />} />
                <Route path="/organizations" element={<RedirQ to="/app/myschoolrx/organizations" />} />
                <Route path="/schools" element={<RedirQ to="/app/myschoolrx/schools" />} />
                <Route path="/onboarding" element={<RedirQ to="/app/myschoolrx/onboarding" />} />
                <Route path="/users" element={<RedirQ to="/app/platform/users" />} />
                <Route path="/auth-health" element={<RedirQ to="/app/platform/auth-health" />} />
                <Route path="/experts" element={<RedirHash path="/app/myschoolrx/cms" hash="#experts" />} />
                <Route path="/risk-bands" element={<RedirQ to="/app/platform/risk-bands" />} />
                <Route path="/cms" element={<RedirQ to="/app/myschoolrx/cms" />} />

                <Route path="*" element={<IndexRedir />} />
              </Routes>
            </main>
          </div>
        </>
      )}
    </div>
  );
}
